"use client";

import React from "react";
import { Avatar, Badge, Box, Flex, Heading, Text } from "@radix-ui/themes";
import styles from "./PageIndexPage.module.scss";
import searchStyles from "../SearchPage/PageSearchResult.module.scss";
import { PageIndexCategory, PageIndexItem } from "./PageIndexPageClient";
import ClickableLink from "../../components/utilities/ClickableLink";
import FontAwesome from "../../components/utilities/FontAwesome";
import CategoryIcon from "../../components/map/CategoryIcon";
import { getCdnUrl } from "../../utils/SchoolBlocksUtilities";

type PageIndexListProps = {
  items: PageIndexItem[];
};

const MAX_VISIBLE_CATEGORIES = 3;

function CategoryBadge({ category }: { category: PageIndexCategory }) {
  return (
    <Badge size="1" variant="soft" radius="full">
      {category.icon_url && (
        <span className={styles.categoryBadgeIcon} aria-hidden="true">
          <CategoryIcon category={category} />
        </span>
      )}
      {category.category_name}
    </Badge>
  );
}

export default function PageIndexList({ items }: PageIndexListProps) {
  if (items.length === 0) {
    return null;
  }

  const elements: React.ReactNode[] = [];
  let previousLetter: string | null = null;

  items.forEach((item, index) => {
    const firstChar = item.title.charAt(0).toUpperCase();
    const firstLetter = /[A-Z]/.test(firstChar) ? firstChar : "#";

    if (firstLetter !== previousLetter) {
      elements.push(
        <Box
          asChild
          key={`header-${firstLetter}`}
          className={styles.letterHeader}
        >
          <li>
            <Heading
              as="h2"
              size="5"
              weight="bold"
              id={`letter-${firstLetter}`}
            >
              {firstLetter}
            </Heading>
          </li>
        </Box>,
      );
      previousLetter = firstLetter;
    }

    elements.push(
      <Flex key={item.id || index} align="start" gap="3" py="2" asChild>
        <li className={searchStyles.result}>
          <Avatar
            size="4"
            radius="small"
            src={item.picture ? getCdnUrl(item.picture) : undefined}
            alt={item.title}
            fallback={
              <FontAwesome prefix="fas" className="fa-file-lines" size={"2x"} />
            }
          />
          <Box flexGrow="1" minWidth="0">
            <ClickableLink href={item.url}>
              <h2>{item.title}</h2>
              <div className={searchStyles.breadcrumbs}>{item.breadcrumbs}</div>
            </ClickableLink>
            {item.categories && item.categories.length > 0 && (
              <Flex gap="1" mt="1" wrap="wrap" align="center">
                {item.categories.slice(0, MAX_VISIBLE_CATEGORIES).map((cat) => (
                  <CategoryBadge key={cat.id} category={cat} />
                ))}
                {item.categories.length > MAX_VISIBLE_CATEGORIES && (
                  <Text size="1" color="gray" weight="medium">
                    +{item.categories.length - MAX_VISIBLE_CATEGORIES} more
                  </Text>
                )}
              </Flex>
            )}
          </Box>
        </li>
      </Flex>,
    );
  });

  return (
    <Box asChild className={styles.list}>
      <ul>{elements}</ul>
    </Box>
  );
}
